import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// ChecklistSharedView schema based on provided response and viewChecklist.ts
const ChecklistSharedViewSchema = z.object({
  id: z.number(),
  title: z.string().nullable(),
  order: z.number().min(0).max(2147483647),
  resource: z.number().nullable(),
  client: z.number().nullable(),
  task: z.number().nullable(),
  can_edit_grant: z.number().refine((val) => [1, 2, 3, 4, 5, 6].includes(val), {
    error: "Can edit grant must be one of [1, 2, 3, 4, 5, 6]"
  }),
  can_view_grant: z.number().refine((val) => [1, 2, 3, 4, 5, 6].includes(val), {
    error: "Can view grant must be one of [1, 2, 3, 4, 5, 6]"
  }),
  as_entity_type: z.string().min(1),
  entity_name: z.string().min(1),
  image_items_count: z.number(),
  comment_items_count: z.number(),
  unchecked_items_count: z.number(),
  assigned_boardusers: z.array(z.any()).optional(), // Adjust type if you have a schema for boardusers
  nudged: z.string().min(1).nullish(),
  _group_value: z.string().nullish(),
  _group_count: z.number().nullish()
});

// Discriminated union for wf and public requests
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string()
});
const ViewChecklistsRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ViewChecklistsRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewChecklistsRequest = z.infer<typeof ViewChecklistsRequestSchema>;
export type ViewChecklistsResponse = z.infer<
  typeof ChecklistSharedViewSchema
>[];

const urls = {
  wf: "/views/:id/checklists",
  public: "/public/:slug/boardviews/:id/checklists"
};

export const useViewChecklists = createQuery<
  ViewChecklistsResponse,
  ViewChecklistsRequest,
  Error
>({
  queryKey: ["viewChecklists"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: apiType, ...rest } = variables;
    return api<Omit<ViewChecklistsRequest, "api">, ViewChecklistsResponse>({
      method: "GET",
      url: urls[apiType],
      requestSchema: ViewChecklistsRequestVariables,
      responseSchema: z.array(ChecklistSharedViewSchema),
      body: rest
    });
  }
});

export const useViewChecklistsSuspense = createSuspenseQuery<
  ViewChecklistsResponse,
  ViewChecklistsRequest,
  Error
>({
  queryKey: ["viewChecklistsSuspense"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: apiType, ...rest } = variables;
    return api<Omit<ViewChecklistsRequest, "api">, ViewChecklistsResponse>({
      method: "GET",
      url: urls[apiType],
      requestSchema: ViewChecklistsRequestVariables,
      responseSchema: z.array(ChecklistSharedViewSchema),
      body: rest
    });
  }
});
